import React, { ChangeEvent } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import { SearchOutlined } from '@mui/icons-material';
import CancelTwoToneIcon from '@mui/icons-material/CancelTwoTone';
import ReplayTwoToneIcon from '@mui/icons-material/ReplayTwoTone';
import { useTranslation } from 'react-i18next';
import { user$ } from '@/store/userStore';
import { Filters } from '@/types/Filters';
import { useTableFilters } from '@/hooks/useTableFilters';
import NoDataFound from '@/components/NoDataFound';
import FilterPopover, {
  FilterOption
} from '@/components/filters/FilterPopover';
import StatusFilter from '@/components/filters/StatusFilter';
import DateFilterBar from '@/components/filters/DateFilterBar';
import { debounce } from '@/utils/helpers';
import { formatDateForApi } from '@/utils/dateFormatters';
import { PosTransactionStatus } from '@/data/posService';
import { PosTransactionWithBranch } from '@/types/PosTransaction.interface';
import { PurchaseType } from '@/enums/purchase-type';
import { PurchaseTypeLabels } from '@/enums/purchase-type-labels';
import {
  posPaymentLegLabelKey,
  summarizeTenderLegs
} from '@/utils/tenderBreakdown';
import {
  getFiscalDocument,
  getInvoiceCustomer,
  getReceivableCustomerMissing,
  summarizeFiscalDocument
} from '@/utils/posFiscalMetadata';
import { maskTaxId } from '@/utils/taxIdMask';
import { isOffDeviceRefundTransaction } from '@/utils/posRefund';

/**
 * All possible PosTransaction.status values, mapped to a Chip color. Kept
 * identical to BranchPosTransactionsManager's STATUS_COLOR so the two
 * listings look the same. Any new status added on the backend must be added
 * here as well or the `default` fallback quietly swallows it.
 */
const STATUS_COLOR: Record<
  PosTransactionStatus,
  'success' | 'error' | 'warning' | 'info' | 'default'
> = {
  APPROVED: 'success',
  APPROVED_NOT_FINALIZED: 'warning',
  PENDING: 'info',
  WAITING_FOR_TERMINAL: 'info',
  UNKNOWN: 'warning',
  FAILED: 'error',
  CANCELLED: 'default',
  TIMEOUT: 'warning',
  REFUNDED: 'default',
  PARTIALLY_REFUNDED: 'warning',
  LOCKED_BY_TERMINAL: 'info',
  MANUAL_OVERRIDE: 'warning'
};

/**
 * Tender chip colors. Deliberately NOT a money-colored scale: `OPEN_ACCOUNT`
 * (Açık Hesap) prints a fiş but collects nothing — the customer leaves owing
 * the branch — so it is shown as a warning rather than alongside the tenders
 * that actually took money. Anything unmapped falls back to `default`.
 */
const PAYMENT_TYPE_COLOR: Partial<
  Record<PurchaseType, 'success' | 'error' | 'warning' | 'info' | 'default'>
> = {
  [PurchaseType.CASH]: 'success',
  [PurchaseType.CARD]: 'info',
  [PurchaseType.PHYSICAL_CARD]: 'info',
  [PurchaseType.MEAL_CARD]: 'info',
  [PurchaseType.WALLET]: 'success',
  [PurchaseType.MIXED]: 'default',
  [PurchaseType.OPEN_ACCOUNT]: 'warning'
};

/**
 * Statuses where a cashier-initiated cancel is still meaningful — after these
 * the sale is either genuinely terminal (APPROVED/FAILED) or already
 * cancelled/timed out. Mirrors BranchPosTransactionsManager.IN_FLIGHT and the
 * backend PosService.cancelTransaction TIMEOUT_CANCEL_ADAPTER_KEYS gate.
 */
const IN_FLIGHT: PosTransactionStatus[] = [
  'PENDING',
  'WAITING_FOR_TERMINAL',
  'UNKNOWN'
];

/**
 * The identifier vendor support (Pavo / Token) actually asks for — the
 * device's own SaleNumber. Pavo returns it in the CheckLinkRequest response,
 * and PosService surfaces it via `providerStatus` on APPROVED transactions
 * (see pavo-pos.adapter.ts mapCheckLink). For non-Pavo providers or
 * non-approved rows it falls back to our own providerReference (UYA-{id}).
 */
const dateFormatter = new Intl.DateTimeFormat('tr-TR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
});

const formatDateTime = (iso: string): string => {
  try {
    return dateFormatter.format(new Date(iso));
  } catch {
    return iso;
  }
};

/**
 * `providerStatus` is NOT always a real sale number even on an APPROVED
 * Pavo transaction: PavoPosAdapter.mapCheckLink sets it to
 * `saleNumber ?? base.providerStatus`, where the fallback is the raw
 * CheckLinkRequest `StatusId` (PavoLinkStatus, always 1-5) — falls back to
 * that bare digit when the terminal response never carried its own
 * SaleNumber (confirmed live 2026-07-30: a debug-mode auto-approval with no
 * genuine card/cash tender left providerStatus="3", i.e. PavoLinkStatus.
 * Completed, masquerading as a sale number). A real Pavo SaleNumber is
 * always a ~10-char alphanumeric id (e.g. "NmtY5vhJKK") — a bare 1-2 digit
 * numeric string is never one, so reject that shape and fall back to our
 * own providerReference instead of showing a misleading link-status code.
 */
const looksLikeRealSaleNumber = (value: string): boolean =>
  !/^\d{1,2}$/.test(value);

const saleNumberOf = (t: PosTransactionWithBranch): string =>
  t.status === 'APPROVED' &&
  t.providerStatus &&
  looksLikeRealSaleNumber(t.providerStatus)
    ? t.providerStatus
    : (t.providerReference ?? `#${t.id}`);

/**
 * The document the device printed for the sale (e-Belge number for a Faturalı
 * sale, otherwise Fiş No / Z No) plus the registered buyer when there is one.
 * Renders nothing for transactions whose metadata carries neither.
 */
const FiscalDocumentInfo = ({
  transaction
}: {
  transaction: PosTransactionWithBranch;
}) => {
  const { t } = useTranslation();
  const summary = summarizeFiscalDocument(getFiscalDocument(transaction));
  const buyer = getInvoiceCustomer(transaction);
  if (!summary && !buyer) return null;

  const receiptText =
    summary?.kind === 'receipt'
      ? [
          summary.receiptNo
            ? `${t('posTransactions.fiscal.receiptNo')}: ${summary.receiptNo}`
            : null,
          summary.zNo
            ? `${t('posTransactions.fiscal.zNo')}: ${summary.zNo}`
            : null
        ]
          .filter(Boolean)
          .join(' / ')
      : null;

  return (
    <>
      {summary?.kind === 'eBelge' && (
        <Box display="flex" alignItems="center" gap={0.5} sx={{ mt: 0.5 }}>
          <Chip
            label={t('posTransactions.fiscal.eBelge')}
            color="info"
            variant="outlined"
            size="small"
          />
          <Typography variant="caption" fontWeight="bold" noWrap>
            {summary.number}
          </Typography>
        </Box>
      )}
      {receiptText && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          noWrap
          sx={{ mt: 0.5 }}
        >
          {receiptText}
        </Typography>
      )}
      {buyer && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          noWrap
        >
          {[buyer.name, maskTaxId(buyer.taxId)].filter(Boolean).join(' · ')}
        </Typography>
      )}
    </>
  );
};

interface PosTransactionsTableProps {
  transactions: PosTransactionWithBranch[];
  loading: boolean;
  totalCount: number;
  onFilterChange: (filters: Filters) => void;
  onCancelTransaction: (transaction: PosTransactionWithBranch) => void;
  onRefundTransaction: (transaction: PosTransactionWithBranch) => void;
  rowsPerPageOptions?: number[];
  pageKey?: string;
}

/**
 * Every `PosTransactionStatus` value, in the order shown in the status
 * filter dropdown — translated via the same `posTransactions.status.*` keys
 * the table's own status Chip already uses.
 */
const STATUS_FILTER_VALUES: PosTransactionStatus[] = [
  'APPROVED',
  'CANCELLED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
  'FAILED',
  'TIMEOUT',
  'PENDING',
  'WAITING_FOR_TERMINAL',
  'UNKNOWN',
  'APPROVED_NOT_FINALIZED'
];

const PosTransactionsTable = ({
  transactions,
  loading,
  totalCount,
  onFilterChange,
  onCancelTransaction,
  onRefundTransaction,
  rowsPerPageOptions = [10, 25, 50, 100],
  pageKey
}: PosTransactionsTableProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    filters,
    setFilters,
    handleSearch: handleSearchFilter,
    handleStatusChange,
    handleDateRangeChange,
    handleApplyFilters,
    handleResetFilters,
    getActiveFiltersCount
  } = useTableFilters({
    initialFilters: {
      status: undefined,
      search: '',
      limit: 25,
      page: 0,
      startDate: undefined,
      endDate: undefined
    },
    onFilterChange,
    pageKey
  });

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    setFilters((prev) => ({ ...prev, page: newPage }));
    onFilterChange({ ...filters, page: newPage });
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const newLimit = parseInt(event.target.value, 10);
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 0 }));
    onFilterChange({ ...filters, page: 0, limit: newLimit });
  };

  const handleSearch = debounce((value: string) => {
    handleSearchFilter(value);
  }, 500);

  const filterOptions: FilterOption[] = [
    {
      id: 'status',
      label: t('filters.status'),
      component: (
        <StatusFilter
          value={filters.status}
          onChange={handleStatusChange}
          options={STATUS_FILTER_VALUES.map((value) => ({
            id: value,
            name: t(`posTransactions.status.${value}`)
          }))}
          size="small"
        />
      )
    },
    {
      id: 'date',
      label: t('filters.date.range'),
      component: (
        <DateFilterBar
          onChange={(dateRange) => {
            if (dateRange) {
              const timezone =
                user$.currentBranch.get()?.timezone ??
                Intl.DateTimeFormat().resolvedOptions().timeZone;
              handleDateRangeChange(
                formatDateForApi(dateRange.startDate),
                formatDateForApi(dateRange.endDate),
                timezone
              );
            } else {
              handleDateRangeChange(undefined, undefined);
            }
          }}
          filterLabel={t('filters.date.range')}
          compact
          showClearButton
          noFilterLabel={t('filters.date.all')}
          size="small"
          initialDateRange={
            filters.startDate && filters.endDate
              ? {
                  startDate: new Date(filters.startDate),
                  endDate: new Date(filters.endDate)
                }
              : undefined
          }
        />
      )
    }
  ];

  const tableHeaders = [
    { id: 'saleNumber', label: t('posTransactions.column.saleNumber') },
    { id: 'createdAt', label: t('posTransactions.column.createdAt') },
    { id: 'company', label: t('posTransactions.column.company') },
    { id: 'branch', label: t('posTransactions.column.branch') },
    { id: 'provider', label: t('posTransactions.column.provider') },
    {
      id: 'amount',
      label: t('posTransactions.column.amount'),
      align: 'right' as const
    },
    {
      id: 'paymentType',
      label: t('posTransactions.column.paymentType'),
      align: 'center' as const
    },
    {
      id: 'status',
      label: t('posTransactions.column.status'),
      align: 'center' as const
    },
    {
      id: 'actions',
      label: t('actions'),
      align: 'center' as const
    }
  ];

  return (
    <Container disableGutters maxWidth={false} sx={{ maxWidth: '95%' }}>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={3}
      >
        <Grid item xs={12}>
          <Card>
            <CardHeader
              action={
                <Box display="flex" alignItems="center">
                  <FormControl sx={{ minWidth: 260, mr: 2 }}>
                    <OutlinedInput
                      placeholder={t('posTransactions.search.placeholder')}
                      defaultValue={filters.search}
                      onChange={(e) => handleSearch(e.target.value)}
                      startAdornment={
                        <InputAdornment position="start">
                          <SearchOutlined />
                        </InputAdornment>
                      }
                      size="small"
                    />
                  </FormControl>

                  <FilterPopover
                    filterOptions={filterOptions}
                    onApplyFilters={handleApplyFilters}
                    onResetFilters={handleResetFilters}
                    activeFiltersCount={getActiveFiltersCount()}
                  />
                </Box>
              }
              title={
                <Box display="flex" alignItems="center">
                  <Typography variant="h4" component="span">
                    {t('posTransactions.title')}
                  </Typography>
                  <Tooltip arrow title={t('refresh.list')}>
                    <IconButton
                      onClick={() => onFilterChange({ ...filters })}
                      sx={{
                        ml: 1,
                        '&:hover': {
                          background: theme.colors.primary.lighter
                        },
                        color: theme.palette.primary.main
                      }}
                    >
                      <RefreshTwoToneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
            <Divider />
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {tableHeaders.map((header) => (
                      <TableCell key={header.id} align={header.align ?? 'left'}>
                        {header.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={tableHeaders.length}>
                        <Box p={2} display="flex" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : transactions.length > 0 ? (
                    transactions.map((tx) => (
                      <TableRow hover key={tx.id}>
                        <TableCell>
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="text.primary"
                            noWrap
                          >
                            {saleNumberOf(tx)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                          >
                            #{tx.id} ·{' '}
                            {t(
                              `posTransactions.targetType.${tx.targetType}`,
                              tx.targetType
                            )}
                          </Typography>
                          <FiscalDocumentInfo transaction={tx} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {formatDateTime(tx.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {tx.branch?.company?.name ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {tx.branch?.name ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap>
                            {tx.provider?.name ?? '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" noWrap>
                            {Number(tx.amount).toFixed(2)} {tx.currency}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {tx.purchaseType ? (
                            <Chip
                              label={t(
                                PurchaseTypeLabels[tx.purchaseType],
                                tx.purchaseType
                              )}
                              color={
                                PAYMENT_TYPE_COLOR[tx.purchaseType] ?? 'default'
                              }
                              variant="outlined"
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                          {getReceivableCustomerMissing(tx) && (
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={t(
                                  'posTransactions.receivableCustomerMissing'
                                )}
                                color="warning"
                                size="small"
                              />
                            </Box>
                          )}
                          {tx.paymentBreakdown &&
                            tx.paymentBreakdown.length > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mt: 0.5 }}
                              >
                                {summarizeTenderLegs(
                                  tx.paymentBreakdown,
                                  (type) => t(posPaymentLegLabelKey(type))
                                )}
                              </Typography>
                            )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t(
                              `posTransactions.status.${tx.status}`,
                              tx.status
                            )}
                            color={STATUS_COLOR[tx.status] ?? 'default'}
                            size="small"
                          />
                          {(tx.recoveryStatus === 'PENDING' ||
                            tx.recoveryStatus === 'MANUAL_REVIEW') && (
                            <Typography
                              variant="caption"
                              color="warning.main"
                              display="block"
                            >
                              {tx.recoveryStatus === 'PENDING'
                                ? 'Ödeme geri alındı; satış kaydı düzeltmesi bekliyor.'
                                : 'Ödeme geri alındı; satış kaydı manuel inceleme gerektiriyor.'}
                            </Typography>
                          )}
                          {tx.providerErrorMessage && (
                            <Tooltip title={tx.providerErrorMessage} arrow>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                noWrap
                                sx={{ maxWidth: 200, mt: 0.5 }}
                              >
                                {tx.providerErrorMessage}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            {tx.status === 'APPROVED' && (
                              <Tooltip
                                title={t(
                                  isOffDeviceRefundTransaction(tx)
                                    ? 'posTransactions.action.offDevice'
                                    : 'posTransactions.action.refund'
                                )}
                                arrow
                              >
                                <IconButton
                                  aria-label={t(
                                    isOffDeviceRefundTransaction(tx)
                                      ? 'posTransactions.action.offDevice'
                                      : 'posTransactions.action.refund'
                                  )}
                                  onClick={() => onRefundTransaction(tx)}
                                  sx={{
                                    '&:hover': {
                                      background: theme.colors.warning.lighter
                                    },
                                    color: theme.palette.warning.main
                                  }}
                                  size="small"
                                >
                                  <ReplayTwoToneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {IN_FLIGHT.includes(tx.status) && (
                              <Tooltip
                                title={t('posTransactions.action.cancel')}
                                arrow
                              >
                                <IconButton
                                  aria-label={t(
                                    'posTransactions.action.cancel'
                                  )}
                                  onClick={() => onCancelTransaction(tx)}
                                  sx={{
                                    '&:hover': {
                                      background: theme.colors.error.lighter
                                    },
                                    color: theme.palette.error.main
                                  }}
                                  size="small"
                                >
                                  <CancelTwoToneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <NoDataFound
                      message={t('posTransactions.no.data')}
                      colSpan={tableHeaders.length}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box p={2}>
              <TablePagination
                component="div"
                count={totalCount || 0}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={filters.page || 0}
                rowsPerPage={filters.limit || 25}
                rowsPerPageOptions={rowsPerPageOptions}
              />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default PosTransactionsTable;
